/**
 * Everything this tenant has uploaded, newest first. Nothing on this screen assumes a
 * kind of document: type and carrier are shown when the parser worked them out and
 * left blank when it did not. Polls while anything is in flight, stops when nothing is.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { get } from "../lib/api";
import type { DocumentList, DocumentSummary } from "../lib/types";
import { Shell } from "../components/Shell";
import { Upload } from "../components/Upload";
import { DocumentsList } from "../components/DocumentsList";
import { StaleBanner } from "../components/StaleBanner";
import { Eyebrow, Metric } from "../components/ui";

export function Documents() {
  // The counted array comes from DocumentsList's own request rather than a second one:
  // one /v1/documents call, one source of truth for what is on the page.
  const [documents, setDocuments] = useState<DocumentSummary[]>([]);

  // The parser version is a one-field read, not a second listing: it comes back on the
  // same envelope and the metric strip needs it for the stale banner.
  const version = useQuery({
    queryKey: ["documents", "parser-version"],
    queryFn: () => get<DocumentList>("/v1/documents?limit=1"),
    staleTime: 60_000,
  });
  const count = {
    total: documents.length,
    flight: documents.filter((d) => d.status === "queued" || d.status === "processing").length,
    done: documents.filter((d) => d.status === "succeeded").length,
    behind: documents.filter((d) => d.current === false).length,
    stale: documents.filter((d) => d.status === "stale").length,
    failed: documents.filter((d) => d.status === "failed").length,
  };

  return (
    <Shell fill crumb={<span className="text-text">documents</span>}>
      <div className="flex shrink-0 flex-wrap items-end justify-between gap-4">
        <div>
          <Eyebrow n="01">intake</Eyebrow>
          <h2 className="mt-2 text-[1.5rem] leading-none tracking-tighter uppercase">Documents</h2>
          <p className="mt-2 max-w-[62ch] text-[12.5px] leading-relaxed text-dim">
            Addressed by content — the same bytes uploaded twice are one document, and
            re-uploading is free.
          </p>
        </div>
        {version.data && (
          <span className="font-mono text-[10.5px] tracking-[0.16em] text-faint uppercase">
            parser {version.data.parser_version}
          </span>
        )}
      </div>

      <Upload />

      <StaleBanner documents={documents} parserVersion={version.data?.parser_version ?? ""} />

      <div className="hairgrid shrink-0 grid-cols-2 lg:grid-cols-5">
        <Metric label="on page" value={count.total} note="on this page" />
        <Metric
          label="in flight"
          value={count.flight}
          valueClass={count.flight ? "text-gold" : ""}
          note="queued or reading"
        />
        <Metric
          label="parsed"
          value={count.done}
          valueClass={count.done ? "text-ok" : ""}
          note="current parser"
        />
        <Metric
          label="behind"
          value={count.behind}
          valueClass={count.behind ? "text-warn" : ""}
          note={count.behind ? "older parser · readable" : "all current"}
        />
        <Metric
          label="failed"
          value={count.failed}
          valueClass={count.failed ? "text-crit" : ""}
          note={count.failed ? "dead-lettered" : "none"}
        />
      </div>

      <DocumentsList onLoaded={setDocuments} />
    </Shell>
  );
}
